import LegalLayout from '../components/LegalLayout';

export default function TermsPage() {
    return (
        <LegalLayout title="Terms of Service" lastUpdated="November 23, 2025">
            <p className="text-gray-300 text-lg mb-8 font-semibold">
                PLEASE READ THESE TERMS OF SERVICE CAREFULLY BEFORE USING THE DAREUP APPLICATION.
            </p>

            <Section title="1. Acceptance of Terms">
                <p>
                    By accessing or using DareUp ("the App"), you agree to be bound by these Terms of Service ("Terms").
                    If you do not agree to these Terms, do not use the App.
                </p>
            </Section>

            <Section title="2. Eligibility">
                <p>
                    You must be at least 13 years old to use DareUp. If you are under 18, you represent that you have
                    your parent or guardian's permission to use the App. By using the App, you represent and warrant
                    that you meet these eligibility requirements.
                </p>
            </Section>

            <Section title="3. Account Registration">
                <Subsection title="3.1 Account Creation:">
                    <p>
                        You must create an account to access certain features. You agree to provide accurate, current,
                        and complete information during registration.
                    </p>
                </Subsection>

                <Subsection title="3.2 Account Security:">
                    <p>
                        You are responsible for maintaining the confidentiality of your account credentials and for all
                        activities under your account.
                    </p>
                </Subsection>

                <Subsection title="3.3 Account Termination:">
                    <p>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
                </Subsection>
            </Section>

            <Section title="4. User Content">
                <Subsection title="4.1 Your Content:">
                    <p>
                        You retain ownership of content you post on DareUp ("User Content"). By posting, you grant us a
                        worldwide, non-exclusive, royalty-free license to use, store, display, and distribute your User
                        Content.
                    </p>
                </Subsection>

                <Subsection title="4.2 Content Standards:">
                    <p>You agree not to post content that:</p>
                    <ul>
                        <li>
                            Is illegal, harmful, threatening, abusive, harassing, defamatory, or invasive of privacy
                        </li>
                        <li>
                            Infringes intellectual property rights or contains copyrighted material without permission
                        </li>
                        <li>Contains viruses or malicious code</li>
                        <li>Promotes violence, discrimination, or illegal activities</li>
                        <li>Is sexually explicit or pornographic</li>
                        <li>Targets or bullies other users</li>
                    </ul>
                </Subsection>

                <Subsection title="4.3 Copyright and Music:">
                    <p>
                        You may not upload videos containing copyrighted music unless you own the rights or have proper
                        authorization. Videos with copyrighted music may have their audio automatically muted. Repeated
                        copyright violations may result in account suspension or termination.
                    </p>
                </Subsection>

                <Subsection title="4.4 Content Moderation:">
                    <p>We reserve the right to remove any content that violates these Terms without notice.</p>
                </Subsection>
            </Section>

            <Section title="5. Prohibited Activities">
                <p>You agree NOT to:</p>
                <ul>
                    <li>Impersonate others or provide false information</li>
                    <li>Harass, bully, or threaten other users</li>
                    <li>Use the App for commercial purposes without authorization</li>
                    <li>Attempt to gain unauthorized access to the App or other accounts</li>
                    <li>Use bots, scrapers, or automated tools</li>
                    <li>Reverse engineer or decompile the App</li>
                    <li>Upload dangerous or harmful dares</li>
                    <li>Spam or send unsolicited messages</li>
                </ul>
            </Section>

            <Section title="6. Dares and Challenges">
                <Subsection title="6.1 User Responsibility:">
                    <p>
                        Users participate in dares at their own risk. DareUp is not responsible for any injuries,
                        damages, or consequences resulting from participating in challenges.
                    </p>
                </Subsection>

                <Subsection title="6.2 Safety:">
                    <p>Do not create or participate in dares that:</p>
                    <ul>
                        <li>Are dangerous or could cause injury</li>
                        <li>Violate laws or regulations</li>
                        <li>Involve illegal substances or activities</li>
                        <li>Put others at risk</li>
                    </ul>
                </Subsection>
            </Section>

            <Section title="7. Intellectual Property & Copyright Policy">
                <Subsection title="7.1 DareUp Property:">
                    <p>
                        The App, including its design, features, and content (excluding User Content), is owned by
                        Amboyance Inc. and protected by copyright, trademark, and other intellectual property laws.
                    </p>
                </Subsection>

                <Subsection title="7.2 Limited License:">
                    <p>
                        We grant you a limited, non-exclusive, non-transferable license to use the App for personal,
                        non-commercial purposes.
                    </p>
                </Subsection>

                <Subsection title="7.3 Copyright Detection:">
                    <p>
                        We use automated systems to detect copyrighted music in videos. Videos containing copyrighted
                        music will have their audio automatically muted to comply with copyright law.
                    </p>
                </Subsection>

                <Subsection title="7.4 DMCA Compliance:">
                    <p>
                        DareUp complies with the Digital Millennium Copyright Act (DMCA). Copyright holders may submit
                        takedown notices for infringing content. We will promptly investigate and remove content that
                        violates copyright law.
                    </p>
                </Subsection>

                <Subsection title="7.5 Copyright Strikes:">
                    <p>Violations of copyright law may result in strikes against your account:</p>
                    <ul>
                        <li>First Strike: Warning and video removed or muted</li>
                        <li>Second Strike: 7-day restriction on uploading new content</li>
                        <li>Third Strike: Permanent ban from uploading videos</li>
                    </ul>
                </Subsection>

                <Subsection title="7.6 Strike Expiration:">
                    <p>Copyright strikes expire 90 days after issuance if no new violations occur.</p>
                </Subsection>

                <Subsection title="7.7 Counter-Notifications:">
                    <p>
                        If you believe your video was incorrectly flagged for copyright infringement, you may file a
                        counter-notification. We will review your claim and may restore the content if the claim is
                        valid.
                    </p>
                </Subsection>

                <Subsection title="7.8 DMCA Agent:">
                    <p>Copyright infringement notices should be sent to:</p>
                    <div className="bg-dark-800 rounded-lg p-4 mt-2">
                        <p>
                            Email:{' '}
                            <a href="mailto:dmca@dareup.com" className="text-primary-400 hover:text-primary-300">
                                dmca@dareup.com
                            </a>
                        </p>
                        <p>Subject: DMCA Takedown Request</p>
                    </div>
                </Subsection>

                <Subsection title="7.9 Fair Use:">
                    <p>
                        While fair use may apply in certain circumstances, the determination is complex and
                        fact-specific. Using copyrighted music without permission generally does not qualify as fair
                        use.
                    </p>
                </Subsection>
            </Section>

            <Section title="8. Privacy">
                <p>
                    Your privacy is important to us. Please review our{' '}
                    <a href="/privacy" className="text-primary-400 hover:text-primary-300">
                        Privacy Policy
                    </a>{' '}
                    to understand how we collect, use, and protect your information.
                </p>
            </Section>

            <Section title="9. Disclaimers">
                <Subsection title='9.1 "AS IS" Basis:'>
                    <p className="uppercase">
                        THE APP IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.
                    </p>
                </Subsection>

                <Subsection title="9.2 No Guarantee:">
                    <p>We do not guarantee that the App will be uninterrupted, secure, or error-free.</p>
                </Subsection>
            </Section>

            <Section title="10. Limitation of Liability">
                <p className="uppercase">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, DAREUP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
                    SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR USE, ARISING FROM
                    YOUR USE OF THE APP.
                </p>
            </Section>

            <Section title="11. Indemnification">
                <p>
                    You agree to indemnify and hold DareUp harmless from any claims, damages, or expenses arising from:
                </p>
                <ul>
                    <li>Your use of the App</li>
                    <li>Your violation of these Terms</li>
                    <li>Your User Content</li>
                    <li>Your violation of any rights of others</li>
                </ul>
            </Section>

            <Section title="12. Changes to Terms">
                <p>
                    We reserve the right to modify these Terms at any time. We will notify users of material changes
                    through the App or via email. Continued use of the App after changes constitutes acceptance of the
                    modified Terms.
                </p>
            </Section>

            <Section title="13. Termination">
                <Subsection title="13.1 By You:">
                    <p>You may delete your account at any time through the App settings.</p>
                </Subsection>

                <Subsection title="13.2 By Us:">
                    <p>
                        We may suspend or terminate your account for violations of these Terms, illegal activity, or at
                        our discretion.
                    </p>
                </Subsection>
            </Section>

            <Section title="14. Dispute Resolution">
                <Subsection title="14.1 Governing Law:">
                    <p>
                        These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada
                        applicable therein.
                    </p>
                </Subsection>

                <Subsection title="14.2 Arbitration:">
                    <p>
                        Any disputes shall be resolved through binding arbitration in Toronto, Ontario, except where
                        prohibited by law.
                    </p>
                </Subsection>
            </Section>

            <Section title="15. Severability">
                <p>
                    If any provision of these Terms is found to be unenforceable, the remaining provisions will continue
                    in full effect.
                </p>
            </Section>

            <Section title="16. Entire Agreement">
                <p>
                    These Terms, along with our{' '}
                    <a href="/privacy" className="text-primary-400 hover:text-primary-300">
                        Privacy Policy
                    </a>
                    , constitute the entire agreement between you and DareUp.
                </p>
            </Section>

            <Section title="17. Contact Us">
                <p>If you have questions about these Terms, please contact us at:</p>
                <div className="bg-dark-800 rounded-xl p-6 mt-4">
                    <p className="font-semibold text-white mb-2">Amboyance Inc.</p>
                    <p>
                        Email:{' '}
                        <a href="mailto:legal@dareup.com" className="text-primary-400 hover:text-primary-300">
                            legal@dareup.com
                        </a>
                    </p>
                    <p>
                        Support:{' '}
                        <a href="mailto:support@dareup.com" className="text-primary-400 hover:text-primary-300">
                            support@dareup.com
                        </a>
                    </p>
                    <p>Address: 2802-99 John St, Toronto, Ontario, M5V 0S6, Canada</p>
                </div>
            </Section>

            <div className="mt-12 pt-8 border-t border-dark-700">
                <p className="text-gray-300">
                    By using DareUp, you acknowledge that you have read, understood, and agree to be bound by these
                    Terms of Service.
                </p>
            </div>
        </LegalLayout>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section className="mb-10">
            <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-4">{title}</h2>
            <div className="text-gray-300 space-y-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&_li]:text-gray-300">
                {children}
            </div>
        </section>
    );
}

function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="mb-4">
            <h3 className="font-semibold text-white mb-2">{title}</h3>
            <div className="[&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-1">{children}</div>
        </div>
    );
}
